import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Users, 
  Building2, 
  Key,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { testDomainSetup, getDelegationInfo } from '../firebase';
import { APP_CONFIG } from '../config/app';

const RoleVerification = () => {
  const { user, userPermissions, userOUs, userRole } = useUser();
  const [verificationResults, setVerificationResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [domainTest, setDomainTest] = useState(null);

  // Required Chrome Admin SDK scopes
  const requiredScopes = [
    'https://www.googleapis.com/auth/admin.directory.user',
    'https://www.googleapis.com/auth/admin.directory.orgunit',
    'https://www.googleapis.com/auth/admin.directory.rolemanagement',
    'https://www.googleapis.com/auth/admin.directory.customer'
  ];

  // Required Google Workspace admin roles
  const requiredRoles = [
    'Super Admin',
    'User Management Admin', 
    'Organisational Unit Admin',
    'Help Desk Admin',
    'Service Admin'
  ];

  // Required permissions for the application
  const requiredPermissions = [
    'user_management',
    'role_assignment', 
    'ou_management',
    'policy_management',
    'view_users',
    'view_ous',
    'view_roles'
  ];

  useEffect(() => {
    if (user) {
      runVerification();
    }
  }, [user, userPermissions, userOUs, userRole]);

  const runVerification = async () => {
    setLoading(true);
    
    try {
      // Test domain-wide delegation
      const domainTestResult = await testDomainSetup();
      setDomainTest(domainTestResult.data);

      // Get delegation info
      const delegationInfo = await getDelegationInfo();
      
      // Verify user permissions
      const permissionChecks = verifyPermissions();
      
      // Verify OAuth scopes (from token)
      const scopeChecks = await verifyOAuthScopes();
      
      // Verify domain access
      const domainChecks = verifyDomainAccess();
      
      // Verify role assignments
      const roleChecks = verifyRoleAssignments();

      setVerificationResults({
        domainTest: domainTestResult.data,
        delegationInfo: delegationInfo.data,
        permissions: permissionChecks,
        scopes: scopeChecks,
        domain: domainChecks,
        roles: roleChecks,
        overall: {
          passed: permissionChecks.passed && scopeChecks.passed && domainChecks.passed && roleChecks.passed,
          score: calculateScore(permissionChecks, scopeChecks, domainChecks, roleChecks)
        }
      });
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationResults({
        error: error.message,
        overall: { passed: false, score: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyPermissions = () => {
    const checks = requiredPermissions.map(permission => ({
      permission,
      required: true,
      granted: userPermissions.includes(permission) || userPermissions.includes('*'),
      description: getPermissionDescription(permission)
    }));

    return {
      checks,
      passed: checks.every(check => check.granted),
      total: checks.length,
      granted: checks.filter(check => check.granted).length
    };
  };

  const verifyOAuthScopes = async () => {
    try {
      const token = await user.getIdTokenResult();
      const claims = token.claims;
      
      // Check if user has admin scopes
      const hasAdminScopes = requiredScopes.some(scope => 
        claims.scope && claims.scope.includes(scope)
      );

      return {
        checks: requiredScopes.map(scope => ({
          scope,
          required: true,
          granted: claims.scope && claims.scope.includes(scope),
          description: getScopeDescription(scope)
        })),
        passed: hasAdminScopes,
        total: requiredScopes.length,
        granted: requiredScopes.filter(scope => 
          claims.scope && claims.scope.includes(scope)
        ).length
      };
    } catch (error) {
      return {
        checks: [],
        passed: false,
        total: requiredScopes.length,
        granted: 0,
        error: error.message
      };
    }
  };

  const verifyDomainAccess = () => {
    const authorizedDomains = [
      'entwinedworkspace.com',
      'entwinedsignage.com', 
      'entwinedworkspaceadmin.com',
      'entwinedsignage.com.au'
    ];

    const userDomain = user?.email?.split('@')[1]?.toLowerCase();
    const isAuthorized = authorizedDomains.includes(userDomain);

    return {
      checks: [{
        domain: userDomain,
        required: true,
        granted: isAuthorized,
        description: `Domain ${userDomain} is authorized`
      }],
      passed: isAuthorized,
      total: 1,
      granted: isAuthorized ? 1 : 0
    };
  };

  const verifyRoleAssignments = () => {
    const hasSuperAdmin = userRole === 'super_admin' || userOUs.includes('*');
    const hasValidRole = userRole && userRole !== 'viewer';
    const hasOUAccess = userOUs && userOUs.length > 0;

    return {
      checks: [
        {
          role: 'Super Admin or Valid Role',
          required: true,
          granted: hasValidRole,
          description: `Current role: ${userRole}`
        },
        {
          role: 'OU Access',
          required: true,
          granted: hasOUAccess,
          description: `Accessible OUs: ${userOUs.join(', ')}`
        }
      ],
      passed: hasValidRole && hasOUAccess,
      total: 2,
      granted: (hasValidRole ? 1 : 0) + (hasOUAccess ? 1 : 0)
    };
  };

  const calculateScore = (permissions, scopes, domain, roles) => {
    const totalChecks = permissions.total + scopes.total + domain.total + roles.total;
    const totalGranted = permissions.granted + scopes.granted + domain.granted + roles.granted;
    return Math.round((totalGranted / totalChecks) * 100);
  };

  const getPermissionDescription = (permission) => {
    const descriptions = {
      'user_management': 'Create, edit, and delete users',
      'role_assignment': 'Assign roles to users',
      'ou_management': 'Create and manage organisational units',
      'policy_management': 'Configure admin policies',
      'view_users': 'View user information',
      'view_ous': 'View organisational units',
      'view_roles': 'View role assignments'
    };
    return descriptions[permission] || permission;
  };

  const getScopeDescription = (scope) => {
    const descriptions = {
      'https://www.googleapis.com/auth/admin.directory.user': 'Manage users',
      'https://www.googleapis.com/auth/admin.directory.orgunit': 'Manage organisational units',
      'https://www.googleapis.com/auth/admin.directory.rolemanagement': 'Manage roles',
      'https://www.googleapis.com/auth/admin.directory.customer': 'Access customer data'
    };
    return descriptions[scope] || scope;
  };

  const getStatusIcon = (passed) => {
    return passed ? (
      <CheckCircle className="h-5 w-5 text-green-500" />
    ) : (
      <XCircle className="h-5 w-5 text-red-500" />
    );
  };

  const getStatusColor = (passed) => {
    return passed ? 'text-green-600' : 'text-red-600';
  };

  // Show simplified view for internal mode
  if (APP_CONFIG.INTERNAL_MODE) {
    return (
      <div className="space-y-6">
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center justify-center p-8">
            <CheckCircle className="h-12 w-12 text-green-500 mr-4" />
            <div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">Internal Mode Active</h3>
              <p className="text-green-700">
                This application is configured for internal workspace use. 
                All domain users have been granted full administrative permissions.
              </p>
              <div className="mt-4 text-sm text-green-600">
                <p><strong>User:</strong> {user?.email}</p>
                <p><strong>Role:</strong> Super Admin (Internal)</p>
                <p><strong>Permissions:</strong> Full Access</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Internal Configuration</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <p>• OAuth scope verification: <span className="text-green-600 font-medium">Bypassed</span></p>
            <p>• Domain-wide delegation: <span className="text-green-600 font-medium">Bypassed</span></p>
            <p>• Permission verification: <span className="text-green-600 font-medium">Bypassed</span></p>
            <p>• Domain access: <span className="text-green-600 font-medium">Required</span></p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600">Verifying permissions...</span>
        </div>
      </div>
    );
  }

  if (!verificationResults) {
    return (
      <div className="card">
        <div className="text-center p-8">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Role Verification</h3>
          <p className="text-gray-600">Click the refresh button to verify your permissions.</p>
          <button
            onClick={runVerification}
            className="btn-primary mt-4"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Verify Permissions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Status */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Shield className="h-6 w-6 mr-2" />
            Role Verification Status
          </h2>
          <button
            onClick={runVerification}
            className="btn-secondary flex items-center"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-900">
              {verificationResults.overall.score}%
            </div>
            <div className="text-sm text-gray-600">Overall Score</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-900">
              {verificationResults.permissions?.granted || 0}/{verificationResults.permissions?.total || 0}
            </div>
            <div className="text-sm text-gray-600">Permissions</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-900">
              {verificationResults.scopes?.granted || 0}/{verificationResults.scopes?.total || 0}
            </div>
            <div className="text-sm text-gray-600">OAuth Scopes</div>
          </div>
        </div>

        <div className="flex items-center justify-center">
          {verificationResults.overall.passed ? (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-8 w-8 mr-2" />
              <span className="text-lg font-semibold">All checks passed! You have the required permissions.</span>
            </div>
          ) : (
            <div className="flex items-center text-red-600">
              <XCircle className="h-8 w-8 mr-2" />
              <span className="text-lg font-semibold">Some checks failed. Please review the details below.</span>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Permissions */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Application Permissions
          </h3>
          <div className="space-y-3">
            {verificationResults.permissions?.checks?.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {getStatusIcon(check.granted)}
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">{check.permission}</div>
                    <div className="text-sm text-gray-600">{check.description}</div>
                  </div>
                </div>
                <span className={`text-sm font-medium ${getStatusColor(check.granted)}`}>
                  {check.granted ? 'Granted' : 'Missing'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* OAuth Scopes */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Key className="h-5 w-5 mr-2" />
            OAuth Scopes
          </h3>
          <div className="space-y-3">
            {verificationResults.scopes?.checks?.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {getStatusIcon(check.granted)}
                  <div className="ml-3">
                    <div className="font-medium text-gray-900 text-sm">
                      {check.scope.split('/').pop()?.replace('.', ' ')}
                    </div>
                    <div className="text-xs text-gray-600">{check.description}</div>
                  </div>
                </div>
                <span className={`text-sm font-medium ${getStatusColor(check.granted)}`}>
                  {check.granted ? 'Granted' : 'Missing'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Domain Access */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="h-5 w-5 mr-2" />
            Domain Access
          </h3>
          <div className="space-y-3">
            {verificationResults.domain?.checks?.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {getStatusIcon(check.granted)}
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">{check.domain}</div>
                    <div className="text-sm text-gray-600">{check.description}</div>
                  </div>
                </div>
                <span className={`text-sm font-medium ${getStatusColor(check.granted)}`}>
                  {check.granted ? 'Authorized' : 'Not Authorized'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Role Assignments */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Role Assignments
          </h3>
          <div className="space-y-3">
            {verificationResults.roles?.checks?.map((check, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {getStatusIcon(check.granted)}
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">{check.role}</div>
                    <div className="text-sm text-gray-600">{check.description}</div>
                  </div>
                </div>
                <span className={`text-sm font-medium ${getStatusColor(check.granted)}`}>
                  {check.granted ? 'Valid' : 'Invalid'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Domain Setup Test */}
      {verificationResults.domainTest && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ExternalLink className="h-5 w-5 mr-2" />
            Domain-Wide Delegation Test
          </h3>
          <div className="p-4 bg-gray-50 rounded-lg">
            {verificationResults.domainTest.success ? (
              <div className="flex items-center text-green-600">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>Domain-wide delegation is working correctly</span>
              </div>
            ) : (
              <div className="flex items-center text-red-600">
                <XCircle className="h-5 w-5 mr-2" />
                <span>Domain-wide delegation setup required</span>
              </div>
            )}
            <div className="mt-2 text-sm text-gray-600">
              <p><strong>Customer ID:</strong> {verificationResults.domainTest.customerId || 'Not found'}</p>
              <p><strong>Domain:</strong> {verificationResults.domainTest.domain || 'Not found'}</p>
              {verificationResults.domainTest.message && (
                <p><strong>Message:</strong> {verificationResults.domainTest.message}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      {!verificationResults.overall.passed && (
        <div className="card bg-yellow-50 border-yellow-200">
          <h3 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            Setup Required
          </h3>
          <div className="text-yellow-700 space-y-4">
            <p>To use this application, you need:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Google Workspace Super Admin or User Management Admin role</li>
              <li>Domain-wide delegation configured for the service account</li>
              <li>OAuth consent screen configured with admin scopes</li>
              <li>Authorized domain access</li>
            </ul>
            
            {/* OAuth Scopes Setup Instructions */}
            {verificationResults.scopes && !verificationResults.scopes.passed && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-yellow-300">
                <h4 className="font-semibold text-yellow-800 mb-2">OAuth Scopes Setup:</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Go to <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console OAuth Consent Screen</a></li>
                  <li>Select your project: <code className="bg-gray-100 px-1 rounded">entwinedworkspaceadmin</code></li>
                  <li>Click "Edit App" and go to "Scopes" section</li>
                  <li>Add these scopes:
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li><code className="bg-gray-100 px-1 rounded text-xs">https://www.googleapis.com/auth/admin.directory.user</code></li>
                      <li><code className="bg-gray-100 px-1 rounded text-xs">https://www.googleapis.com/auth/admin.directory.orgunit</code></li>
                      <li><code className="bg-gray-100 px-1 rounded text-xs">https://www.googleapis.com/auth/admin.directory.rolemanagement</code></li>
                      <li><code className="bg-gray-100 px-1 rounded text-xs">https://www.googleapis.com/auth/admin.directory.customer</code></li>
                    </ul>
                  </li>
                  <li>Add authorized origins:
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      <li><code className="bg-gray-100 px-1 rounded text-xs">https://entwinedworkspaceadmin.web.app</code></li>
                      <li><code className="bg-gray-100 px-1 rounded text-xs">http://localhost:3000</code></li>
                    </ul>
                  </li>
                  <li>Save and re-authenticate</li>
                </ol>
              </div>
            )}
            
            <p className="mt-4">
              <strong>Contact your Google Workspace administrator</strong> to set up the required permissions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleVerification;
